import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PharmacySlideshowBackground from '../components/PharmacySlideshowBackground';
import { Search, MapPin, GraduationCap, Star, Phone, Mail, Award, Users } from 'lucide-react';

interface College {
  id: number;
  name: string;
  shortName: string;
  location: string;
  state: string;
  approvals: string[];
  rating: number;
  reviewCount: number;
  bestIn: string;
  courses: string[];
  type: 'Government' | 'Private';
  phone?: string;
  email?: string;
  address: string;
}

const pharmacyColleges: College[] = [
  {
    id: 1,
    name: "Annamalai University",
    shortName: "AU",
    location: "Cuddalore",
    state: "Tamil Nadu",
    approvals: ["UGC", "NAAC"],
    rating: 4.2,
    reviewCount: 268,
    bestIn: "Research Excellence",
    courses: ["B.Pharm", "M.Pharm", "Pharm.D"],
    type: "Government",
    phone: "(04144) 232282",
    email: "info@annamalaiuniversity.ac.in",
    address: "Annamalainagar, Cuddalore, Tamil Nadu"
  },
  {
    id: 2,
    name: "Madras Medical College",
    shortName: "MMC",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["MCI", "UGC"],
    rating: 4.5,
    reviewCount: 892,
    bestIn: "Medical Integration",
    courses: ["B.Pharm", "M.Pharm"],
    type: "Government",
    phone: "(044) 25305000",
    address: "EVR Periyar Salai, Park Town, Chennai, Tamil Nadu"
  },
  {
    id: 3,
    name: "Madurai Medical College",
    shortName: "MMC",
    location: "Madurai",
    state: "Tamil Nadu",
    approvals: ["MCI", "UGC"],
    rating: 4.3,
    reviewCount: 567,
    bestIn: "Clinical Pharmacy",
    courses: ["B.Pharm", "M.Pharm", "D.Pharm"],
    type: "Government",
    phone: "(0452) 2533235",
    address: "Post Office- Gandhi Nagar, Madurai, Tamil Nadu"
  },
  {
    id: 4,
    name: "The Tamil Nadu Dr. M.G.R. Medical University",
    shortName: "TNMGRMU",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["UGC", "MCI"],
    rating: 4.4,
    reviewCount: 734,
    bestIn: "Medical Education",
    courses: ["B.Pharm", "M.Pharm", "Pharm.D"],
    type: "Government",
    phone: "(044) 22353574",
    email: "registrar@tnmgrmu.ac.in",
    address: "No. 69, Anna Salai, Guindy, Chennai, Tamil Nadu"
  },
  // Private Colleges
  {
    id: 5,
    name: "Aadhi Bhagawan College of Pharmacy",
    shortName: "ABCP",
    location: "Tiruvannamalai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 45,
    bestIn: "Academics",
    fees: "₹ 85,000",
    course: "B.Pharm",
    type: "Private",
    address: "Rantham Village, Cheyyar Tk, Tiruvannamalai - 604 407"
  },
  {
    id: 6,
    name: "Adhiparasakthi College of Pharmacy",
    shortName: "ACP",
    location: "Kancheepuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.8,
    reviewCount: 67,
    bestIn: "Infrastructure",
    fees: "₹ 90,000",
    course: "B.Pharm",
    type: "Private",
    address: "Melmaruvathur, Kancheepuram-603319"
  },
  {
    id: 7,
    name: "Annai J.K.K. Sampoorani Ammal College of Pharmacy",
    shortName: "AJKKSACP",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 89,
    bestIn: "Placements",
    fees: "₹ 82,000",
    course: "B.Pharm",
    type: "Private",
    address: "P.B.No.70 Ethirmedu, B.Kumarapalayam, Namakkal District– 638 183"
  },
  {
    id: 8,
    name: "Annai Veilankanni's Pharmacy College",
    shortName: "AVPC",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 124,
    bestIn: "Faculty",
    fees: "₹ 95,000",
    course: "B.Pharm",
    type: "Private",
    address: "81, V.G.P. Salai, Saidapet, Chennai-600 015"
  },
  {
    id: 9,
    name: "Apollo College of Pharmacy",
    shortName: "ACP",
    location: "Kancheepuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 4.1,
    reviewCount: 156,
    bestIn: "Industry Exposure",
    fees: "₹ 120,000",
    course: "B.Pharm",
    type: "Private",
    address: "Mevalurkuppam Village, Sriperumbadur TK, Kancheepuram 602105"
  },
  {
    id: 10,
    name: "Arulmigu Kalasalingam College of Pharmacy",
    shortName: "AKCP",
    location: "Virudhunagar",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.9,
    reviewCount: 98,
    bestIn: "Research",
    fees: "₹ 88,000",
    course: "B.Pharm",
    type: "Private",
    address: "Anand Nagar, Krishnankoil, Srivilliputur (Via), Virudhunagar 626 126"
  },
  {
    id: 11,
    name: "Arunai College of Pharmacy",
    shortName: "ACP",
    location: "Tiruvannamalai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 78,
    bestIn: "Academics",
    fees: "₹ 75,000",
    course: "B.Pharm",
    type: "Private",
    address: "Velu Nagar, Thenmathur, Tiruvannamalai - 606 603"
  },
  {
    id: 12,
    name: "C.L.Baid Metha College of Pharmacy",
    shortName: "CLBMCP",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 4.0,
    reviewCount: 145,
    bestIn: "Infrastructure",
    fees: "₹ 110,000",
    course: "B.Pharm",
    type: "Private",
    address: "Jyothi Nagar, 2/305, Rajiv Gandhi Salai, OMR, Thoraipakkam, Chennai-600097"
  },
  {
    id: 13,
    name: "Cherraan's College of Pharmacy",
    shortName: "CCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 87,
    bestIn: "Placements",
    fees: "₹ 92,000",
    course: "B.Pharm",
    type: "Private",
    address: "521-A, Siruvani Main Road, Telungupalayam Pirivu, Coimbatore -641 039"
  },
  {
    id: 14,
    name: "Dhanalakshmi Srinivasan College of Pharmacy",
    shortName: "DSCP",
    location: "Perambalur",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 102,
    bestIn: "Faculty",
    fees: "₹ 85,000",
    course: "B.Pharm",
    type: "Private",
    address: "Thuraiyur Road, Perambalur - 621 212"
  },
  {
    id: 15,
    name: "Dr. Kalam College of Pharmacy",
    shortName: "DKCP",
    location: "Thanjavur",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 56,
    bestIn: "Research",
    fees: "₹ 80,000",
    course: "B.Pharm",
    type: "Private",
    address: "Periyanayagipuram Village, Avanam Post, Peravurani Tk, Thanjavur - 614 623"
  },
  {
    id: 12,
    name: "E.R.K. College of Pharmacy",
    shortName: "ERKCP",
    location: "Dharmapuri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 43,
    bestIn: "Academics",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "Erumiyampatti (V), Kokkarapatti (P), Pappireddipatti (T), Dharmapuri 636905"
  },
  {
    id: 13,
    name: "Edayathangudy G.S. Pillay College of Pharmacy",
    shortName: "EGSPCP",
    location: "Nagapattinam",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 38,
    bestIn: "Infrastructure",
    fees: "₹ 76,000",
    course: "B.Pharm",
    type: "Private",
    address: "Old Nagore Road, Nagapattinam-611 002"
  },
  {
    id: 14,
    name: "Excel College of Pharmacy",
    shortName: "ECP",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 72,
    bestIn: "Placements",
    fees: "₹ 84,000",
    course: "B.Pharm",
    type: "Private",
    phone: "9965511199",
    address: "NH 544, Ranganoor Road, Sankari West (PO), Kumarapalayam - 637 303"
  },
  {
    id: 15,
    name: "G.P. Pharmacy College",
    shortName: "GPCP",
    location: "Tirupattur",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 29,
    bestIn: "Academics",
    fees: "₹ 72,000",
    course: "B.Pharm",
    type: "Private",
    address: "49, Vaniyambadi Main Road, Mandalavadi (V&P), Jolarpettai, Tirupattur dist 635851"
  },
  {
    id: 16,
    name: "G.R.D. College of Pharmacy",
    shortName: "GRDCP",
    location: "Thiruvallur",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 67,
    bestIn: "Infrastructure",
    fees: "₹ 86,000",
    course: "B.Pharm",
    type: "Private",
    address: "Pudhur (V), Thiruvallur-Thiruthani NH205 Road, Kunnavalam (P), Thiruvallur dist-631210"
  },
  {
    id: 17,
    name: "GRT Institute of Pharmaceutical Education & Research",
    shortName: "GRTIPER",
    location: "Tiruttani",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.8,
    reviewCount: 94,
    bestIn: "Research",
    fees: "₹ 98,000",
    course: "B.Pharm",
    type: "Private",
    address: "GRT Mahalakshmi nagar, Chennai-Tirupati Highway, Tiruttani- 631209"
  },
  {
    id: 18,
    name: "Immanuel Arasar College of Pharmacy",
    shortName: "IACP",
    location: "Kanniyakumari",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 41,
    bestIn: "Faculty",
    fees: "₹ 74,000",
    course: "B.Pharm",
    type: "Private",
    address: "Edaivilagam, Nattalam, Marthandam, Kanniyakumari -629195"
  },
  {
    id: 19,
    name: "J.K.K. Munirajah Institute of Health Sciences",
    shortName: "JKKMIHSCP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 115,
    bestIn: "Placements",
    fees: "₹ 89,000",
    course: "B.Pharm",
    type: "Private",
    address: "Thookanaickenapalayam (PO), Gobi (TK), Erode - 638 506"
  },
  {
    id: 20,
    name: "J.K.K. Nattraja College of Pharmacy",
    shortName: "JKKNCP",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.8,
    reviewCount: 128,
    bestIn: "Infrastructure",
    fees: "₹ 91,000",
    course: "B.Pharm",
    type: "Private",
    address: "Natarajapuram, NH-544, Kumarapalayam, Namakkal- 638 183"
  },
  {
    id: 21,
    name: "Jaya College of Paramedical Sciences",
    shortName: "JCPS",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 52,
    bestIn: "Academics",
    fees: "₹ 88,000",
    course: "B.Pharm",
    type: "Private",
    address: "Thirunindravur, Chennai-602 024"
  },
  {
    id: 22,
    name: "K.K. College of Pharmacy",
    shortName: "KKCP",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 83,
    bestIn: "Faculty",
    fees: "₹ 92,000",
    course: "B.Pharm",
    type: "Private",
    address: "1/161 Sankaralinganar Road, Gerugambakkam, Chennai-128"
  },
  {
    id: 23,
    name: "K.M. College of Pharmacy",
    shortName: "KMCP",
    location: "Madurai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 76,
    bestIn: "Placements",
    fees: "₹ 85,000",
    course: "B.Pharm",
    type: "Private",
    address: "Melur Road, Uthangudi, Madurai-625107"
  },
  {
    id: 24,
    name: "Kamalakshi Pandurangan College of Pharmacy",
    shortName: "KPCP",
    location: "Thiruvannamalai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 47,
    bestIn: "Infrastructure",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "Ayyampalayam, Thiruvannamalai-606603"
  },
  {
    id: 25,
    name: "Kamarajar College of Pharmacy",
    shortName: "KCP",
    location: "Cuddalore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 58,
    bestIn: "Research",
    fees: "₹ 81,000",
    course: "B.Pharm",
    type: "Private",
    address: "1071, Thiruppaninatham, Keerapalayam, Chidambaram, Cuddalore dist-608602"
  },
  {
    id: 26,
    name: "Karpagam College of Pharmacy",
    shortName: "KCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.9,
    reviewCount: 142,
    bestIn: "Industry Exposure",
    fees: "₹ 105,000",
    course: "B.Pharm",
    type: "Private",
    address: "S.F.No.762, Othakkal Mandapam, Pollachi Main Road, Coimbatore-641 032"
  },
  {
    id: 27,
    name: "Kasthooribha Gandhi Pharmacy College",
    shortName: "KGPC",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 35,
    bestIn: "Academics",
    fees: "₹ 77,000",
    course: "B.Pharm",
    type: "Private",
    address: "59/3, Moolakadu, Masakalipatty Post, Rasipuram Taluk, Namakkal-637401"
  },
  {
    id: 28,
    name: "KG College of Pharmacy & RI",
    shortName: "KGCPRI",
    location: "Villupuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 62,
    bestIn: "Research",
    fees: "₹ 83,000",
    course: "B.Pharm",
    type: "Private",
    address: "Ulagalampoondi Road, Ashokapuri Post, Villupuram Dist-605203"
  },
  {
    id: 29,
    name: "KMCH College of Pharmacy",
    shortName: "KMCHCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 4.2,
    reviewCount: 187,
    bestIn: "Healthcare Integration",
    fees: "₹ 125,000",
    course: "B.Pharm",
    type: "Private",
    address: "S.F.No: 940/1A & 1B, Kovai Estate, Kalapatti Road, Coimbatore – 641 048"
  },
  {
    id: 30,
    name: "Krishna College of Pharmacy",
    shortName: "KCP",
    location: "Trichy",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 69,
    bestIn: "Faculty",
    fees: "₹ 86,000",
    course: "B.Pharm",
    type: "Private",
    address: "Kottaimedu, Irungalur, Manachanallur Tk., Trichy-621105"
  },
  {
    id: 31,
    name: "Mayor Radhakrishnan College of Pharmacy",
    shortName: "MRCP",
    location: "Cuddalore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 55,
    bestIn: "Research",
    fees: "₹ 82,000",
    course: "B.Pharm",
    type: "Private",
    address: "1, Devanampattinam Road, Devanampattinam, Cuddalore-607001"
  },
  {
    id: 32,
    name: "Mohamed Sathak A.J. College of Pharmacy",
    shortName: "MSAJCP",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 96,
    bestIn: "Infrastructure",
    fees: "₹ 95,000",
    course: "B.Pharm",
    type: "Private",
    address: "Medavakkam Road, Sholinganallur, Chennai - 119"
  },
  {
    id: 33,
    name: "Mother Teresa College of Pharmacy",
    shortName: "MTCP",
    location: "Pudukottai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 42,
    bestIn: "Faculty",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "Mettusalai, Illupur Post, Pudukottai-622102"
  },
  {
    id: 34,
    name: "Nandha College of Pharmacy",
    shortName: "NCP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 89,
    bestIn: "Placements",
    fees: "₹ 87,000",
    course: "B.Pharm",
    type: "Private",
    address: "Koorapalayam Pirivu, Pitchandampalayam, Erode-638052"
  },
  {
    id: 35,
    name: "Nandhanam Health Science College",
    shortName: "NHSC",
    location: "Vellore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 31,
    bestIn: "Academics",
    fees: "₹ 75,000",
    course: "B.Pharm",
    type: "Private",
    address: "Nandhanam Nagar, Molagarampatti, Tirupattur, Vellore Dist.-635602"
  },
  {
    id: 36,
    name: "P.S.V. College of Pharmaceutical Science & Research",
    shortName: "PSVCPSR",
    location: "Krishnagiri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 58,
    bestIn: "Research",
    fees: "₹ 80,000",
    course: "B.Pharm",
    type: "Private",
    address: "Bangalore-Chennai Highway, NH 46, Orappam Village, Bargur taluk, Krishnagiri-635108"
  },
  {
    id: 37,
    name: "Paavai College of Pharmacy and Research",
    shortName: "PCPR",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 73,
    bestIn: "Infrastructure",
    fees: "₹ 84,000",
    course: "B.Pharm",
    type: "Private",
    address: "NH 44, R.Pulliyapatti, Puduchatram Post, Namakkal-637018"
  },
  {
    id: 38,
    name: "Pachamuthu College of Pharmacy",
    shortName: "PCP",
    location: "Dharmapuri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 46,
    bestIn: "Faculty",
    fees: "₹ 77,000",
    course: "B.Pharm",
    type: "Private",
    address: "178/3A, Muthupatti, Hale Dharmapuri Post, Krishnagiri Main Road, Dharmapuri-636701"
  },
  {
    id: 39,
    name: "Padmavathi College of Pharmacy",
    shortName: "PCP",
    location: "Dharmapuri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 52,
    bestIn: "Academics",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "Krishnagiri Main Road, Periyanahalli P.O, Dharmapuri-635 205"
  },
  {
    id: 40,
    name: "Pallavan Pharmacy College",
    shortName: "PPC",
    location: "Kancheepuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 38,
    bestIn: "Infrastructure",
    fees: "₹ 76,000",
    course: "B.Pharm",
    type: "Private",
    address: "Iyyengarkulam Post, Kancheepuram-631502"
  },
  {
    id: 41,
    name: "Pannai College of Pharmacy",
    shortName: "PCP",
    location: "Dindigul",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 44,
    bestIn: "Research",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "Mullipadi, Trichy road, Dindigul-624005"
  },
  {
    id: 42,
    name: "PERI College of Pharmacy",
    shortName: "PERI",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 87,
    bestIn: "Industry Exposure",
    fees: "₹ 92,000",
    course: "B.Pharm",
    type: "Private",
    address: "PERI Knowledge Park, Mannivakkam, Chennai-600048"
  },
  {
    id: 43,
    name: "Periyar College of Pharmaceutical Sciences",
    shortName: "PCPS",
    location: "Trichirapalli",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 71,
    bestIn: "Academics",
    fees: "₹ 83,000",
    course: "B.Pharm",
    type: "Private",
    address: "Periyar Centenary Educational Complex, K.Sathanoor Main Road, Tiruchirapalli-620021"
  },
  {
    id: 44,
    name: "PGP College of Pharmaceutical Sciences and Research Institute",
    shortName: "PGPCPSRI",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 59,
    bestIn: "Research",
    fees: "₹ 81,000",
    course: "B.Pharm",
    type: "Private",
    address: "NH-7, Namakkal - Karur Main Road, Namakkal - 637 207"
  },
  {
    id: 45,
    name: "PPG College of Pharmacy",
    shortName: "PPGCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 94,
    bestIn: "Placements",
    fees: "₹ 89,000",
    course: "B.Pharm",
    type: "Private",
    address: "209, Sathy Road, Saravanampatti, Coimbatore-641035"
  },
  {
    id: 46,
    name: "PSG College of Pharmacy",
    shortName: "PSGCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 4.3,
    reviewCount: 198,
    bestIn: "Excellence",
    fees: "₹ 135,000",
    course: "B.Pharm",
    type: "Private",
    address: "P.B.No.1674, Avanashi Road, Peelamedu, Coimbatore - 641 004"
  },
  {
    id: 47,
    name: "R.V.S College of Pharmaceutical Sciences",
    shortName: "RVSCPS",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.8,
    reviewCount: 103,
    bestIn: "Faculty",
    fees: "₹ 96,000",
    course: "B.Pharm",
    type: "Private",
    address: "242–B, Trichy Road, Sulur, Coimbatore – 641 402"
  },
  {
    id: 48,
    name: "RKP College of Pharmacy",
    shortName: "RKPCP",
    location: "Krishnagiri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 36,
    bestIn: "Infrastructure",
    fees: "₹ 74,000",
    course: "B.Pharm",
    type: "Private",
    address: "Peddanapalli Village, Periyakottapalli Post, Krishnagiri-635001"
  },
  {
    id: 49,
    name: "RVS Padmavathi College of Pharmacy",
    shortName: "RVSPCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 78,
    bestIn: "Research",
    fees: "₹ 88,000",
    course: "B.Pharm",
    type: "Private",
    address: "Kumaran Kottam Campus, Unit IV, Kannamapalayam, Coimbatore-641402"
  },
  {
    id: 50,
    name: "S.A. Raja Pharmacy College",
    shortName: "SARPC",
    location: "Tirunelveli",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 47,
    bestIn: "Academics",
    fees: "₹ 77,000",
    course: "B.Pharm",
    type: "Private",
    address: "Raja Nagar, Vadakangulam, Tirunelveli Dt 627116"
  },
  {
    id: 51,
    name: "Sankaralingam Bhuvaneswari College of Pharmacy",
    shortName: "SBCP",
    location: "Sivakasi",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 53,
    bestIn: "Faculty",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "3/77 c, Anaikuttam Road, Anakuttam, Sivakasi-626130"
  },
  {
    id: 52,
    name: "Senghundhar College of Pharmacy",
    shortName: "SCP",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 66,
    bestIn: "Placements",
    fees: "₹ 82,000",
    course: "B.Pharm",
    type: "Private",
    address: "Sathinaickanpalayam, Kumaramangalam Post, Tiruchengode, Namakkal-637205"
  },
  {
    id: 53,
    name: "Shree Krishna College of Pharmacy",
    shortName: "SKCP",
    location: "Thiruvannamalai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 41,
    bestIn: "Infrastructure",
    fees: "₹ 76,000",
    course: "B.Pharm",
    type: "Private",
    address: "Bangalore Main Road, Pakkeripalayam, Chengam Taluk, Thiruvannamalai-606709"
  },
  {
    id: 54,
    name: "Shree Sastha College of Pharmacy",
    shortName: "SSCP",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.8,
    reviewCount: 108,
    bestIn: "Industry Exposure",
    fees: "₹ 98,000",
    course: "B.Pharm",
    type: "Private",
    address: "Chennai-Bangalore Highway, Sree Sastha Nagar, Chembarabakkam, Chennai-600123"
  },
  {
    id: 55,
    name: "Shree Venkateshwara College of Pharmacy",
    shortName: "SVCP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 57,
    bestIn: "Academics",
    fees: "₹ 80,000",
    course: "B.Pharm",
    type: "Private",
    address: "Erode-Gobi Main Road, Sri Kalaivani Nagar, Othakuthirai, K.Mettupalayam Post, Gobi, Erode-638455"
  },
  {
    id: 56,
    name: "Shri Indra Ganesan Institute of Medical Science",
    shortName: "SIGIMS",
    location: "Trichy",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 68,
    bestIn: "Medical Integration",
    fees: "₹ 85,000",
    course: "B.Pharm",
    type: "Private",
    address: "Madurai Main Road, Manikandam Union, Srirangam Taluk, Trichy - 620012"
  },
  {
    id: 57,
    name: "Sir Issac Newton College of Pharmacy",
    shortName: "SINCP",
    location: "Nagapattinam",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 34,
    bestIn: "Research",
    fees: "₹ 75,000",
    course: "B.Pharm",
    type: "Private",
    address: "Pappakovil, Anthanapettai Post, Nagapattinam-611102"
  },
  {
    id: 58,
    name: "Smt.Gandhimathi College of Pharmacy",
    shortName: "SGCP",
    location: "Thiruvannamalai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 48,
    bestIn: "Faculty",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "SF. No. 68/2D, (59/1 & 59/2 ), Nallan Pillai Petral, Villupuram Road, Pallikondapattu post, Thiruvannamallai- 606611"
  },
  {
    id: 59,
    name: "SNS College of Pharmacy",
    shortName: "SNSCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.9,
    reviewCount: 127,
    bestIn: "Innovation",
    fees: "₹ 105,000",
    course: "B.Pharm",
    type: "Private",
    address: "SNS Kalvi Nagar, Saravanampatti, Coimbatore-641035"
  },
  {
    id: 60,
    name: "Sree Abirami College of Pharmacy",
    shortName: "SACP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 82,
    bestIn: "Infrastructure",
    fees: "₹ 87,000",
    course: "B.Pharm",
    type: "Private",
    address: "Machegoundanpalayam, Seerapalayam Village, Eachanari post, Coimbatore-641021"
  },
  {
    id: 61,
    name: "Sree Bhavani College of Pharmacy",
    shortName: "SBCP",
    location: "Cuddalore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 56,
    bestIn: "Academics",
    fees: "₹ 81,000",
    course: "B.Pharm",
    type: "Private",
    address: "NH 532, Veppur-Virudhachalam Main Road, Kandappankurichi, Veppur Taluk, Cuddalore Dist-606302"
  },
  {
    id: 62,
    name: "Sri Lakshminarayan College of Pharmacy",
    shortName: "SLCP",
    location: "Dharmapuri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 39,
    bestIn: "Faculty",
    fees: "₹ 76,000",
    course: "B.Pharm",
    type: "Private",
    address: "Pappinaicknahalli Village, Sogathur Post, Pennagaram Road, Dharmapuri - 636 809"
  },
  {
    id: 63,
    name: "Sri Ram Nallamani Yadhava College of Pharmacy",
    shortName: "SRNYCP",
    location: "Tirunelveli",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 44,
    bestIn: "Research",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "Nallamani Nagar, Kodikurichi, Tenkasi, Tirunelveli 627804"
  },
  {
    id: 64,
    name: "Sri Ramakrishna College of Pharmacy",
    shortName: "SRCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 4.0,
    reviewCount: 156,
    bestIn: "Excellence",
    fees: "₹ 115,000",
    course: "B.Pharm",
    type: "Private",
    address: "Sri Ramakrishna Institute of Para Medical Sciences, 395, Sarojini Naidu Road, Coimbatore -641 044"
  },
  {
    id: 65,
    name: "Sri Rangapoopathi College of Pharmacy",
    shortName: "SRCP",
    location: "Villupuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 46,
    bestIn: "Infrastructure",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "Alampoondi, Gingee Taluk Villupuram District 604151"
  },
  {
    id: 66,
    name: "Sri Shanmugha College of Pharmacy",
    shortName: "SSCP",
    location: "Salem",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 73,
    bestIn: "Placements",
    fees: "₹ 84,000",
    course: "B.Pharm",
    type: "Private",
    phone: "9865462217",
    address: "Sankari - Tiruchengode Main Road, Pullipalayam, Sankari TK, Salem - 637 304"
  },
  {
    id: 67,
    name: "Sri Vijay Vidayala College of Pharmacy",
    shortName: "SVVCP",
    location: "Dharmapuri",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 35,
    bestIn: "Academics",
    fees: "₹ 75,000",
    course: "B.Pharm",
    type: "Private",
    address: "Nallampalli P.O & TK, Dharmapuri - 636 807"
  },
  {
    id: 68,
    name: "Srinivasan College of Pharmaceutical Sciences",
    shortName: "SCPS",
    location: "Trichy",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 88,
    bestIn: "Research",
    fees: "₹ 86,000",
    course: "B.Pharm",
    type: "Private",
    address: "NH 45, Chennai-Trichy Trunk road, Samayapuram, Trichy 621112"
  },
  {
    id: 69,
    name: "SS Institute of Pharmacy",
    shortName: "SSIP",
    location: "Salem",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 61,
    bestIn: "Faculty",
    fees: "₹ 80,000",
    course: "B.Pharm",
    type: "Private",
    address: "216, Manjakkalpatty, Kuppanur Post, Sankari tk., Salem-637301"
  },
  {
    id: 70,
    name: "S.S.M. College of Pharmacy",
    shortName: "SSMCP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 67,
    bestIn: "Infrastructure",
    fees: "₹ 83,000",
    course: "B.Pharm",
    type: "Private",
    address: "S.F.No.834/1&2, Chinniyampalayam, Jambai Bhavani Tk, Erode -638 312"
  },
  {
    id: 71,
    name: "St.Mariam College of Pharmacy",
    shortName: "SMCP",
    location: "Tirunelveli",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 49,
    bestIn: "Academics",
    fees: "₹ 77,000",
    course: "B.Pharm",
    type: "Private",
    address: "Tenkasi-Tirunelveli Main Road, Pudur, Tirunelveli-627851"
  },
  {
    id: 72,
    name: "Sun College of Pharmacy & Research Centre",
    shortName: "SCPRC",
    location: "Kanyakumari",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 54,
    bestIn: "Research",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "Udhaya Nagar, Vellamodi, Ammandi Vilai PO, Kanyakumari District-629204"
  },
  {
    id: 73,
    name: "Surya School of Pharmacy",
    shortName: "SSP",
    location: "Villupuram",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 71,
    bestIn: "Placements",
    fees: "₹ 82,000",
    course: "B.Pharm",
    type: "Private",
    address: "G.S.T. Road, nh 45, Vikravandi, Villupuram District-605652"
  },
  {
    id: 74,
    name: "Swamy Vivekanandha College of Pharmacy",
    shortName: "SVCP",
    location: "Namakkal",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 89,
    bestIn: "Excellence",
    fees: "₹ 87,000",
    course: "B.Pharm",
    type: "Private",
    address: "Elayampalayam, Tiruchengode, Namakkal-637205. (Angammal Trust)"
  },
  {
    id: 75,
    name: "Tagore College of Pharmacy",
    shortName: "TCP",
    location: "Chennai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.7,
    reviewCount: 95,
    bestIn: "Infrastructure",
    fees: "₹ 91,000",
    course: "B.Pharm",
    type: "Private",
    address: "Vandalur - Kelambakkam Main Road, Rathinamangalam, Chennai - 600127"
  },
  {
    id: 76,
    name: "Thanthai Roever College of Pharmacy",
    shortName: "TRCP",
    location: "Perambalur",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 58,
    bestIn: "Academics",
    fees: "₹ 81,000",
    course: "B.Pharm",
    type: "Private",
    address: "Roever Campus, Perambalur -621 212"
  },
  {
    id: 77,
    name: "The Erode College of Pharmacy",
    shortName: "TECP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 45,
    bestIn: "Faculty",
    fees: "₹ 78,000",
    course: "B.Pharm",
    type: "Private",
    address: "Perundurai Main Road, Veppampalayam, Vallipurathanpalayam P.O, Erode - 638 112"
  },
  {
    id: 78,
    name: "Ultra College of Pharmacy",
    shortName: "UCP",
    location: "Madurai",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.5,
    reviewCount: 69,
    bestIn: "Placements",
    fees: "₹ 84,000",
    course: "B.Pharm",
    type: "Private",
    address: "4/235, College Road, Thasildar Nagar, Madurai - 625 020"
  },
  {
    id: 79,
    name: "United College of Pharmacy (UIT)",
    shortName: "UCP",
    location: "Coimbatore",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.4,
    reviewCount: 63,
    bestIn: "Technology",
    fees: "₹ 82,000",
    course: "B.Pharm",
    type: "Private",
    address: "No.2, Gudalur Village, Periyanaickenpalayam, Coimbatore-641 020"
  },
  {
    id: 80,
    name: "Vellalar College of Pharmacy",
    shortName: "VCP",
    location: "Erode",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.3,
    reviewCount: 51,
    bestIn: "Infrastructure",
    fees: "₹ 79,000",
    course: "B.Pharm",
    type: "Private",
    address: "Maruthi Nagar, Thindal Post, Erode-638012"
  },
  {
    id: 81,
    name: "Vivekanandha Pharmacy College for Women",
    shortName: "VPCW",
    location: "Salem",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.6,
    reviewCount: 74,
    bestIn: "Women Empowerment",
    fees: "₹ 85,000",
    course: "B.Pharm",
    type: "Private",
    address: "Veerachipalayam, Sankari West, Sankari Taluk, Salem 637303, (Rabindranath Tagore Trust)"
  },
  {
    id: 82,
    name: "Jainee College of Pharmacy",
    shortName: "JCP",
    location: "Dindigul",
    state: "Tamil Nadu",
    approvals: ["AICTE", "PCI"],
    rating: 3.2,
    reviewCount: 37,
    bestIn: "Academics",
    fees: "₹ 76,000",
    course: "B.Pharm",
    type: "Private",
    address: "Dindigul Theni Road, Veerakkal Bpo, N Panjampatti (Po), Dindigul 624303"
  }
];

const PharmacyPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'rating'>('name');
  const [filterByType, setFilterByType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Removed unused itemsToShow and maxItemsPerLoad

  // Debounce search term to prevent performance issues
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Ensure unique IDs for all colleges to prevent rendering issues
  const collegesWithUniqueIds = pharmacyColleges.map((college, index) => ({
    ...college,
    id: index + 1 // Assign sequential IDs starting from 1
  }));

  // Filter colleges based on search and filters using debounced search
  const filteredColleges = collegesWithUniqueIds.filter(college => {
    const matchesSearch = !debouncedSearchTerm || 
      college.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      college.location?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      college.course?.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    const matchesType = filterByType === '' || college.type === filterByType;
    return matchesSearch && matchesType;
  });

  // Sort colleges
  const sortedColleges = [...filteredColleges].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;

      default:
        return a.name.localeCompare(b.name);
    }
  });

  // Manual pagination for better performance with large datasets
  const itemsPerPage = 10; // Show 10 items per page
  const [currentPage, setCurrentPage] = useState(1);
  
  // Calculate pagination values
  const totalItems = sortedColleges.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedColleges = sortedColleges.slice(startIndex, endIndex);

  // Pagination handlers with loading state for better UX
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setIsLoading(true);
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setIsLoading(false);
      }, 50);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setIsLoading(true);
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
        setIsLoading(false);
      }, 50);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setIsLoading(true);
      setTimeout(() => {
        setCurrentPage(page);
        setIsLoading(false);
      }, 50);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setIsLoading(true);
    // Add a small delay to show loading state
    const timer = setTimeout(() => {
      setCurrentPage(1);
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [debouncedSearchTerm, filterByType, sortBy]);

  return (
    <>
      {/* Slideshow section at the very top */}
      <div className="w-full h-96">
        <PharmacySlideshowBackground />
      </div>

      {/* Animated Pharmacy Admissions Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg min-h-[60px]">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: 0-2 Counseling Result was Postponed</span>
            <span>•</span>
            <span>📢 FUTURE NOTICE: TN Medical Selection Will be Updated Soon</span>
            <span>•</span>
            <span>🎓 PHARMACY ADMISSIONS 2025-26 NOW OPEN!</span>
            <span>•</span>
            <span>💊 B.Pharm & D.Pharm Programs Available</span>
            <span>•</span>
            <span>🏥 Top Government & Private Pharmacy Colleges</span>
            <span>•</span>
            <span>📞 Get Expert Guidance: +91 77086 77132</span>
            <span>•</span>
            <span>💰 Scholarship Opportunities Available</span>
            <span>•</span>
            <span>📧 Contact: infonammasewa@gmail.com</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* Main content below slideshow, pushed downward */}
      <div className="min-h-screen bg-gray-50 relative overflow-hidden pt-8">
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <h1 className="text-3xl font-bold text-black mb-2 flex items-center">
              <GraduationCap className="w-8 h-8 mr-2 text-black" />
              Top Pharmacy Colleges in Tamil Nadu
            </h1>
            <p className="text-lg text-gray-700 mb-6">Explore the best government and private pharmacy colleges, their courses, fees, ratings, and more.</p>
            {/* Search and Filter Controls */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-6">
              <div className="flex items-center mb-2 md:mb-0">
                <Search className="w-5 h-5 text-gray-500 mr-2" />
                <input
                  type="text"
                  placeholder="Search by college, location, or course..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-64 focus:outline-none focus:ring focus:border-blue-400"
                />
              </div>
              <div className="flex items-center mb-2 md:mb-0">
                <label className="mr-2 font-medium text-gray-700">Type:</label>
                <select
                  title="Filter by college type"
                  value={filterByType}
                  onChange={e => setFilterByType(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring focus:border-blue-400"
                >
                  <option value="">All</option>
                  <option value="Government">Government</option>
                  <option value="Private">Private</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="mr-2 font-medium text-gray-700">Sort by:</label>
                <select
                  title="Sort colleges by"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as 'name' | 'rating')}
                  className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring focus:border-blue-400"
                >
                  <option value="name">Name</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
            </div>
            {/* College Table */}
            <div className="overflow-x-auto bg-white rounded shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Location</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Courses Offered</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Rating</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-blue-900">Best In</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-blue-600 font-semibold">Loading...</td>
                    </tr>
                  ) : paginatedColleges.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-gray-500">No colleges found.</td>
                    </tr>
                  ) : (
                    paginatedColleges.map(college => (
                      <tr key={college.id} className="hover:bg-blue-50 transition">
                        <td className="px-4 py-2 font-medium text-blue-900">
                          {college.name}
                          <div className="text-xs text-gray-500">{college.shortName}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1 text-blue-400" />
                            {college.location}, {college.state}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${college.type === 'Government' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{college.type}</span>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {college.courses.map((course, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{course}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="flex items-center">
                            <Star className="w-4 h-4 mr-1 text-yellow-400" />
                            {college.rating} <span className="ml-1 text-xs text-gray-500">({college.reviewCount})</span>
                          </span>
                        </td>
                        <td className="px-4 py-2">{college.bestIn}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1 || isLoading}
                className={`px-4 py-2 rounded bg-blue-200 text-blue-900 font-semibold mr-2 ${currentPage === 1 || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>
              <span className="text-blue-900 font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || isLoading}
                className={`px-4 py-2 rounded bg-blue-200 text-blue-900 font-semibold ml-2 ${currentPage === totalPages || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PharmacyPage;